import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertiesByState, normalizeAbbr } from './propertyUtils';

interface CityEntry {
  name: string;
  slug: string;
  stateSlug: string;
  stateAbbr: string;
  image: string;
  href: string;
}

const CITIES_PER_PAGE_DESKTOP = 4;
const CITIES_PER_PAGE_MOBILE = 2;
const GRID_HEIGHT = 500;
const SWIPE_THRESHOLD = 50;

export function CommunitiesSlider() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchDeltaRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [isMobile]);

  const propertiesByState = getPropertiesByState();

  const cities = useMemo<CityEntry[]>(() => {
    const entries: CityEntry[] = [];
    for (const state of Object.values(propertiesByState)) {
      for (const city of Object.values(state.cities)) {
        const abbr = normalizeAbbr(state.abbr || state.name);
        const stateUrlSegment = abbr.toLowerCase();
        const firstProp = city.properties?.[0] as any;
        entries.push({
          name: city.name,
          slug: city.slug,
          stateSlug: stateUrlSegment,
          stateAbbr: abbr,
          image: firstProp?.property_featured_image || '',
          href: `/apartments/${stateUrlSegment}/${city.slug}/`,
        });
      }
    }
    return entries.sort((a, b) => a.name.localeCompare(b.name));
  }, [propertiesByState]);

  const citiesPerPage = isMobile ? CITIES_PER_PAGE_MOBILE : CITIES_PER_PAGE_DESKTOP;

  const pages = useMemo(() => {
    const result: CityEntry[][] = [];
    for (let i = 0; i < cities.length; i += citiesPerPage) {
      result.push(cities.slice(i, i + citiesPerPage));
    }
    return result;
  }, [cities, citiesPerPage]);

  const totalPages = pages.length;

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
  }, [totalPages]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
    touchDeltaRef.current = { x: 0, y: 0 };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaRef.current = {
      x: e.targetTouches[0].clientX - touchStartRef.current.x,
      y: e.targetTouches[0].clientY - touchStartRef.current.y,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    const { x: dx, y: dy } = touchDeltaRef.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx < 0) {
        goToPage(currentPage + 1);
      } else {
        goToPage(currentPage - 1);
      }
    }
  }, [currentPage, goToPage]);

  if (!cities.length) return null;

  const trackStyle = isMobile
    ? { transform: `translateX(-${currentPage * 100}%)` }
    : { transform: `translateY(-${currentPage * GRID_HEIGHT}px)` };

  return (
    <section className="case-communities">
      <div className="case-container">
        <div style={{ display: 'block' }}>
          <EditableToken path="communitiesslider.label" type="text" label="Section Label" defaultValue={contentSchema.tokens['communitiesslider.label'].defaultValue} as="p" style={{ textTransform: 'uppercase', fontWeight: 600, paddingBottom: '0.75rem' }}>
            {useContentToken('communitiesslider.label')}
          </EditableToken>
        </div>
        <div style={{ display: 'block' }}>
          <EditableToken path="communitiesslider.title" type="text" label="Title" defaultValue={contentSchema.tokens['communitiesslider.title'].defaultValue} as="h2" style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 50 }}>
            {useContentToken('communitiesslider.title')}
          </EditableToken>
        </div>
      </div>
      <div className="case-container-wide">
        <div className="case-communities-slider">
          <div
            className="case-communities-viewport"
            onTouchStart={isMobile ? handleTouchStart : undefined}
            onTouchMove={isMobile ? handleTouchMove : undefined}
            onTouchEnd={isMobile ? handleTouchEnd : undefined}
          >
            <div
              className={`case-communities-track${isMobile ? ' _mobile' : ''}`}
              style={trackStyle}
            >
              {pages.map((page, pageIdx) => {
                if (isMobile) {
                  return (
                    <div key={pageIdx} className="case-communities-grid _mobile-page">
                      {page.map((c) => (
                        <div key={c.href} className="case-community-card">
                          <a href={c.href} className="community-card-image">
                            {c.image ? (
                              <VibesitesImage url={c.image} alt={`${c.name}, ${c.stateAbbr}`} sizes="50vw" />
                            ) : (
                              <div className="community-card-placeholder">{c.name}</div>
                            )}
                          </a>
                          <div className="community-card-text">
                            <a href={c.href} style={{ textDecoration: 'none' }}>
                              <span className="case-community-card-label">{c.name}, {c.stateAbbr}</span>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                const isEven = pageIdx % 2 === 1;
                return (
                  <div
                    key={pageIdx}
                    className={`case-communities-grid${isEven ? ' _even' : ''}`}
                  >
                    {page.map((c, i) => {
                      let sizeClass = '';
                      if (page.length >= 4) {
                        if (isEven) {
                          if (i === 2) sizeClass = '_lg';
                          else if (i === 3) sizeClass = '_md';
                        } else {
                          if (i === 0) sizeClass = '_lg';
                          else if (i === 3) sizeClass = '_md';
                        }
                      }
                      return (
                        <div key={c.href} className={`case-community-card ${sizeClass}`}>
                          <a href={c.href} className="community-card-image">
                            {c.image ? (
                              <VibesitesImage url={c.image} alt={`${c.name}, ${c.stateAbbr}`} sizes="(min-width: 768px) 25vw, 50vw" />
                            ) : (
                              <div className="community-card-placeholder">
                                {c.name}
                              </div>
                            )}
                          </a>
                          <div className="community-card-text">
                            <a href={c.href} style={{ textDecoration: 'none' }}>
                              <span className="case-community-card-label">{c.name}, {c.stateAbbr}</span>
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          {totalPages > 1 && (
            <div className={`case-communities-pagination${isMobile ? ' _mobile-dots' : ''}`}>
              {pages.map((_, i) => (
                <button
                  key={i}
                  className={`case-communities-dot${i === currentPage ? ' _active' : ''}`}
                  onClick={() => setCurrentPage(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '0.75rem 1.5rem 0' }}>
          <a className="case-btn" href="/apartments/">View Communities</a>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunitiesSlider',
  displayName: 'Communities Slider',
  tokens: {
    'communitiesslider.label': {
      label: 'Section Label',
      type: 'text',
      defaultValue: 'OUR COMMUNITIES',
    },
    'communitiesslider.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Find the best fit for you',
    },
  },
};

(CommunitiesSlider as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommunitiesSlider;
